import { useEffect, useState } from 'react';
import type { DeviceRow, OutletRef, RegisterRef, SyncReviewRow } from '@kassa/shared';
import { api, ApiError } from '../../api';
import { useConfirm } from '../../components/ConfirmDialog';
import { approveInput, reasonText } from '../../devices';
import { formatMoney } from '../../format';
import { DeviceConnect } from './DeviceConnect';
import { DesktopDownload } from './DesktopDownload';
import { LanEvents } from './LanEvents';

// Cihazlar (KT-79, dizayn: Rollar.dc.html «Təsdiq gözləyən cihaz», «Təsdiqlənmiş cihazlar»).
// Təsdiqdə rol seçilir: kassa (kassaya bağlanır, bir kassaya bir cihaz) və ya ana kompüter (obyektdə bir).


function when(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

const KIND: Record<DeviceRow['kind'], string> = { desktop: 'masaüstü proqram', browser: 'brauzer', tablet: 'planşet', phone: 'telefon' };

export function DevicesPanel({ tenantId }: { tenantId?: string }) {
  const confirm = useConfirm();
  const [devices, setDevices] = useState<DeviceRow[]>([]);
  const [registers, setRegisters] = useState<RegisterRef[]>([]);
  const [outlets, setOutlets] = useState<OutletRef[]>([]);
  const [reviews, setReviews] = useState<SyncReviewRow[]>([]);
  const [choice, setChoice] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = () =>
    Promise.all([api.devices(), api.deviceReviews()])
      .then(([d, r]) => {
        setDevices(d);
        setReviews(r);
      })
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Cihazlar yüklənmədi'))
      .finally(() => setLoaded(true));

  useEffect(() => {
    load();
    api.shiftRegisters().then(setRegisters).catch(() => setError('Kassalar siyahısı yüklənmədi — təsdiq üçün səhifəni yeniləyin'));
    api.outlets().then(setOutlets).catch(() => setError('Obyektlər siyahısı yüklənmədi — təsdiq üçün səhifəni yeniləyin'));
  }, []);

  async function act(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Əməliyyat alınmadı');
    } finally {
      setBusy(false);
    }
  }

  const approve = (d: DeviceRow) => {
    const input = approveInput(choice[d.id]);
    if (!input) {
      setError(`«${d.name}» üçün kassa və ya ana kompüter seçin`);
      return;
    }
    act(() => api.deviceApprove(d.id, input));
  };

  const revoke = async (d: DeviceRow, pending: boolean) => {
    const text = pending ? `«${d.name}» rədd edilsin?` : `«${d.name}» ləğv edilsin? Cihaz dərhal sinxron edə bilməyəcək.`;
    if (!(await confirm(text, { confirmLabel: pending ? 'Rədd et' : 'Ləğv et', danger: true }))) return;
    act(() => api.deviceRevoke(d.id));
  };

  const regName = (id: string | null) => registers.find((r) => r.id === id)?.name ?? 'kassa';
  const outletName = (id: string | null) => outlets.find((o) => o.id === id)?.name ?? 'obyekt';
  const pending = devices.filter((d) => d.status === 'pending');
  const approved = devices.filter((d) => d.status === 'approved');

  return (
    <section className="card" aria-label="Cihazlar">
      <h2>Cihazlar</h2>
      {error && (
        <p className="bad" role="alert">
          {error}
        </p>
      )}
      {!loaded && <p className="muted">Yüklənir…</p>}
      <DesktopDownload />
      <LanEvents />
      {tenantId && <DeviceConnect tenantId={tenantId} onRegistered={() => void load()} />}

      {pending.map((d) => (
        <div key={d.id} className="device-pending" role="group" aria-label={`Təsdiq gözləyən cihaz ${d.name}`}>
          <p>
            <strong>Təsdiq gözləyən cihaz:</strong> {d.name}
          </p>
          <p className="muted small">
            {[d.platform, KIND[d.kind], d.appVersion ? `versiya ${d.appVersion}` : null].filter(Boolean).join(' · ')} · {when(d.createdAt)}
          </p>
          <label className="field">
            <span>Harada işləyəcək</span>
            <select value={choice[d.id] ?? ''} onChange={(e) => setChoice({ ...choice, [d.id]: e.target.value })} disabled={busy}>
              <option value="">— seçin —</option>
              <optgroup label="Kassa (kassaya bağlanır)">
                {registers.map((r) => (
                  <option key={r.id} value={`cash:${r.id}`}>
                    {r.name} ({r.code}) · {r.outletName}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Ana kompüter (obyektdə bir)">
                {outlets.map((o) => (
                  <option key={o.id} value={`main:${o.id}`}>
                    {o.name}
                  </option>
                ))}
              </optgroup>
            </select>
          </label>
          <span className="toolbar">
            <button type="button" className="btn-primary btn-small" onClick={() => approve(d)} disabled={busy}>
              Təsdiqlə
            </button>
            <button type="button" className="btn-small" onClick={() => revoke(d, true)} disabled={busy}>
              Rədd et
            </button>
          </span>
        </div>
      ))}

      <h3>Təsdiqlənmiş cihazlar</h3>
      <ul className="product-list">
        {approved.map((d) => (
          <li key={d.id} className="product-row">
            <span className="product-name">{d.name}</span>
            <span className="muted small">
              {d.role === 'cash' ? regName(d.registerId) : d.role === 'main' ? `ana kompüter · ${outletName(d.outletId)}` : 'rol yoxdur'} · son əlaqə {when(d.lastSeenAt)}
            </span>
            <span className="toolbar">
              <button type="button" className="link-btn" onClick={() => revoke(d, false)} disabled={busy}>
                Ləğv et
              </button>
            </span>
          </li>
        ))}
      </ul>
      {loaded && approved.length === 0 && <p className="muted">Təsdiqlənmiş cihaz yoxdur.</p>}

      {reviews.length > 0 && (
        <>
          <h3>Yoxlanmalı internetsiz satışlar</h3>
          <p className="muted small">Cihazın dəyərləri (müştərinin ödədiyi) yazılıb; server hesabı fərqlidir və ya qayda pozulub.</p>
          <ul className="product-list">
            {reviews.map((r) => (
              <li key={r.docId} className="product-row">
                <span className="product-name mono">{r.displayNo}</span>
                <span className="mono">{formatMoney(Number(r.total))}</span>
                <span className="muted small">
                  {reasonText(r.reasons)} · {r.deviceName ?? 'cihaz'} · {when(r.soldAt)}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
